import { FieldProps } from 'enums/CreateProductModalEnums';
import React, { ReactNode, useState, KeyboardEvent } from 'react';
import COSelect from '../COSelect';
import styles from './CreateProductModal.module.scss';
import DatePickerField from '../DatePickerField';
import { Moment } from 'moment';
import { DatePickerProps } from 'enums/DatePickerEnum';
import InputField from '../InputField';
import { RangeValue } from 'rc-picker/lib/interface';
import { Row } from 'antd';
import { DESCRIPTION_ITALIC_TEXT } from './CreateProductModalConstants';
import { useFormikContext } from 'formik';
import { CategoryOptions } from './ProductModalForm';

export interface Field {
  key: string;
  name: string;
  placeholder?: string;
  label?: string | ReactNode;
  options?: CategoryOptions[];
  select?: boolean;
  date?: boolean;
  size?: string;
  info?: boolean;
  handleOpenModal?: () => void;
  disabled?: boolean;
  onChange?: (value: string) => void;
  onKeyUp?: (event: KeyboardEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  showSearch?: boolean;
  handleFilter?: (optionA: CategoryOptions, optionB: CategoryOptions) => number;
  optionFilterProp?: string;
  render?: (menu: ReactNode) => ReactNode;
}

interface CreateProductFieldsProps {
  fields: Field[];
  activeYears?: number[];
}

const CreateProductFields = ({
  fields,
  activeYears,
}: CreateProductFieldsProps) => {
  const [_, setSelectedDate] = useState<RangeValue<Moment>>();
  const { setFieldValue } = useFormikContext();

  const disabledDate = (date: Moment) => !activeYears?.includes(date.year());

  return (
    <Row>
      {fields.map((field: Field) => {
        const {
          select,
          size,
          name,
          placeholder,
          options,
          label,
          render,
          info,
          handleOpenModal,
          disabled,
          onChange,
          onKeyUp,
          showSearch,
          optionFilterProp,
          date,
          onKeyDown,
        } = field;

        const handleSelectOnChange = (value: string) => {
          if (name === FieldProps.PROCESSING_FACILITY)
            setFieldValue(FieldProps.REPORTING_YEAR, undefined);
          onChange && onChange(value);
        };

        return select && size === FieldProps.SIZE_BIG ? (
          <div className={styles.inputContainer}>
            <COSelect
              key={name}
              name={name}
              placeholderstring={placeholder ?? ''}
              options={options}
              label={label as string}
              dropdownRender={render}
              info={info}
              infoClick={handleOpenModal}
              disabled={disabled}
              onChange={handleSelectOnChange}
              onKeyUp={onKeyUp}
              onKeyDown={onKeyDown}
              showSearch={showSearch}
              optionFilterProp={optionFilterProp}
            />
          </div>
        ) : date && size === FieldProps.SIZE_BIG ? (
          <div className={styles.inputContainer}>
            <DatePickerField
              key={name}
              picker={DatePickerProps.YEAR}
              name={name}
              placeholder={placeholder}
              label={label as string}
              info={info}
              infoClick={handleOpenModal}
              disabled={disabled}
              disabledDate={disabledDate}
              setSelectedDate={setSelectedDate}
              onChange={(date: Moment) => {
                setFieldValue(name, date.year());
              }}
              showReportingPeriod
              initialYear={2012}
            />
          </div>
        ) : select && size === FieldProps.SIZE_SMALL ? (
          <div className={styles.smallInputContainer}>
            <COSelect
              key={name}
              name={name}
              label={label as string}
              placeholderstring={placeholder ?? ''}
              options={options}
              info={info}
              infoClick={handleOpenModal}
              disabled={disabled}
            />
          </div>
        ) : size === FieldProps.SIZE_MEDIUM ? (
          <div className={styles.mediumInputContainer}>
            <InputField
              key={name}
              name={name}
              label={label}
              placeholderstring={placeholder ?? ''}
              info={info}
              infoClick={handleOpenModal}
              disabled={disabled}
            />
          </div>
        ) : size === FieldProps.SIZE_EXTRA_BIG ? (
          <div className={styles.extraBigInputContainer}>
            <InputField
              textarea
              italicText={DESCRIPTION_ITALIC_TEXT}
              key={name}
              name={name}
              label={label}
              placeholderstring={placeholder ?? ''}
              info={info}
              infoClick={handleOpenModal}
              disabled={disabled}
            />
          </div>
        ) : (
          <div className={styles.inputContainer}>
            <InputField
              key={name}
              name={name}
              label={label}
              placeholderstring={placeholder ?? ''}
              info={info}
              infoClick={handleOpenModal}
              disabled={disabled}
            />
          </div>
        );
      })}
    </Row>
  );
};

export default CreateProductFields;
